import React from "react";
import { useAudioPlayer as useBaseAudioPlayer } from "react-use-audio-player";

export type PlayerProps = {
  isLoading: boolean;
  play: () => void;
  pause: () => void;
  position: number;
  duration: number;
  seekTo: (seconds: number) => void;
  isPlaying: boolean;
  rate: number;
  setRate: (rate: number) => void;
};

export const useAudioPlayer = (url: string): PlayerProps => {
  const {
    load,
    pause,
    play: basePlay,
    getPosition,
    duration,
    seek,
    playing: isPlaying,
    rate,
    setRate,
  } = useBaseAudioPlayer();
  const [isLoading, setIsLoading] = React.useState(false);
  const [position, setPosition] = React.useState(0);

  // Load the audio when URL changes
  React.useEffect(() => {
    if (url) {
      setIsLoading(true);
      // The format will be automatically detected from the file extension
      load(url, {
        autoplay: false,
        html5: true,
        onload: () => setIsLoading(false),
      });
    }
  }, [url, load]);

  // Update position every second when playing and handle end of audio
  React.useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isPlaying) {
      interval = setInterval(() => {
        const currentPosition = getPosition();
        setPosition(currentPosition);

        // Check if we've reached the end
        if (currentPosition >= (duration || 0)) {
          pause();
          seek(0);
          setPosition(0);
        }
      }, 500);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isPlaying, getPosition, duration, pause, seek]);

  // Wrapper for play to handle loading state
  const play = React.useCallback(() => {
    if (!isLoading) {
      basePlay();
    }
  }, [isLoading, basePlay]);

  // Seek to a specific position in seconds
  const seekTo = React.useCallback(
    (seconds: number) => {
      if (!isLoading && seconds >= 0 && seconds <= (duration || 0)) {
        seek(seconds);
        setPosition(seconds);
      }
    },
    [isLoading, duration, seek]
  );

  return {
    isLoading,
    play,
    pause,
    position,
    duration: duration || 0,
    seekTo,
    isPlaying,
    rate,
    setRate,
  };
};

export default useAudioPlayer;
